import type { ReactNode } from "react";

import { cn } from "@/utils/utils";

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  /** A count beside the label. Zero still shows — "0" is information. */
  count?: number;
  disabled?: boolean;
}

/**
 * An underline tab strip.
 *
 * Underline rather than pills: pills read as buttons, and a row of buttons
 * where only one is filled makes the unselected ones look disabled. Arrow keys
 * move between tabs, as they do everywhere else that calls itself a tablist.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the strip for a screen reader — "Member sections", say. */
  label: string;
  className?: string;
}) {
  const move = (delta: number) => {
    const usable = items.filter((item) => !item.disabled);
    const index = usable.findIndex((item) => item.value === value);
    const next = usable[(index + delta + usable.length) % usable.length];
    if (next) onChange(next.value);
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn("hairline-b flex min-w-0 gap-1 overflow-x-auto", className)}
      onKeyDown={(event) => {
        if (event.key === "ArrowRight") move(1);
        else if (event.key === "ArrowLeft") move(-1);
        else return;
        event.preventDefault();
      }}
    >
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.value)}
            className={cn(
              "text-callout relative -mb-px flex shrink-0 items-center gap-2 rounded-t-md px-3 pt-2 pb-2.5",
              "border-b-2 font-medium transition-colors duration-[var(--duration-fast)]",
              "disabled:pointer-events-none disabled:opacity-40",
              selected
                ? "border-accent text-fg"
                : "text-fg-muted hover:text-fg border-transparent",
            )}
          >
            {item.label}
            {item.count === undefined ? null : (
              <span
                className={cn(
                  "text-footnote rounded-full px-1.5 py-px tabular-nums",
                  selected ? "bg-accent-soft text-accent" : "bg-surface-inset text-fg-muted",
                )}
              >
                {item.count.toLocaleString()}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A segmented control: two or three mutually exclusive views of the same data,
 * where the choice is a lens rather than a destination.
 */
export function Segmented<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: readonly { value: T; label: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        "bg-surface-inset inline-flex shrink-0 gap-0.5 rounded-[10px] p-0.5",
        className,
      )}
    >
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(item.value)}
            className={cn(
              "text-subhead rounded-lg px-3 py-1.5 font-medium",
              "transition-[background-color,color,box-shadow] duration-[var(--duration-fast)]",
              selected
                ? "bg-surface-raised text-fg shadow-[var(--shadow-lift)]"
                : "text-fg-muted hover:text-fg",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
